'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import RelatedBlogCarousel from '@/components/RelatedBlogCarousel';

export default function BlogPost() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-4">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              How Our Hosting Service Can <span className="text-transparent bg-clip-text bg-gradient-rainbow">Improve Your Website's Speed and Performance</span>
            </motion.h1>
            <motion.div 
              className="text-xl text-slate-300 max-w-3xl mx-auto flex items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p>By 1 Dollar Cloud</p>
              <p>Published On: November 24, 2024</p>
              <p>Categories: Web Hosting</p>
            </motion.div>
          </div>
        </div>
      </section>
  
        {/* Featured Image */}
        <section className="relative bg-slate-900 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="/assets/blog-images/hosting-performance.svg"
                alt="Hosting Performance Illustration"
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </section>

      {/* Blog Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300">In today's digital world, website speed and performance are crucial factors that directly impact user experience, search engine rankings, and conversion rates. At 1 Dollar Cloud, we understand the importance of delivering lightning-fast websites to your visitors. Let's explore how our hosting service can significantly improve your website's speed and performance.</p>
            </div>

            {[
              {
                title: "Advanced Server Infrastructure",
                content: [
                  "**State-of-the-Art Hardware:** Our servers are equipped with the latest generation processors, NVMe SSDs, and high-speed RAM to ensure optimal performance.",
                  "**Global Data Centers:** Strategically located data centers around the world minimize latency and provide faster access to your website.",
                  "**Network Optimization:** Our infrastructure includes:",
                  "• Enterprise-grade networking equipment",
                  "• Multiple tier-1 bandwidth providers",
                  "• Advanced routing algorithms"
                ]
              },
              {
                title: "Performance-Focused Technologies",
                content: [
                  "**Caching Solutions:**",
                  "• Server-side caching",
                  "• Object caching",
                  "• Database query caching",
                  "**Content Delivery:**",
                  "• Built-in CDN integration",
                  "• Dynamic content optimization",
                  "• Automatic image optimization"
                ]
              },
              {
                title: "Resource Management",
                content: [
                  "**Optimized Resource Allocation:**",
                  "• Dedicated CPU cores",
                  "• Guaranteed RAM allocation",
                  "• SSD storage prioritization",
                  "**Load Balancing:**",
                  "• Automatic traffic distribution",
                  "• Real-time scaling",
                  "• High availability setup"
                ]
              },
              {
                title: "Security Without Performance Impact",
                content: [
                  "**Smart Security Measures:**",
                  "• DDoS protection without latency",
                  "• Real-time threat detection",
                  "• SSL/TLS optimization",
                  "**Firewall Configuration:**",
                  "• Intelligent traffic filtering",
                  "• Zero-day attack prevention",
                  "• Performance-optimized rules"
                ]
              },
              {
                title: "Database Optimization",
                content: [
                  "**Database Performance:**",
                  "• Query optimization",
                  "• Index management",
                  "• Regular maintenance",
                  "**Connection Management:**",
                  "• Pool optimization",
                  "• Load distribution",
                  "• Resource monitoring"
                ]
              },
              {
                title: "Built-in Performance Tools",
                content: [
                  "**Monitoring and Analytics:**",
                  "• Real-time performance metrics",
                  "• Resource usage tracking",
                  "• Performance bottleneck detection",
                  "**Optimization Tools:**",
                  "• One-click optimization",
                  "• Custom configuration options",
                  "• Performance recommendations"
                ]
              },
              {
                title: "Regular Maintenance and Updates",
                content: [
                  "**Proactive Maintenance:**",
                  "• Regular software updates",
                  "• Security patches",
                  "• Performance optimizations",
                  "**System Health Checks:**",
                  "• Automated monitoring",
                  "• Preventive maintenance",
                  "• Quick issue resolution"
                ]
              },
              {
                title: "Why Choose 1 Dollar Cloud",
                content: [
                  "**Our Advantages:**",
                  "• Affordable pricing starting at $1/month",
                  "• Enterprise-grade infrastructure",
                  "• 24/7 expert support",
                  "• 99.9% uptime guarantee",
                  "**Customer Benefits:**",
                  "• Faster website loading times",
                  "• Improved user experience",
                  "• Better search engine rankings",
                  "• Increased conversion rates"
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                ))}
              </motion.div>
            ))}
          
            
          </motion.div>
          
        </div>
      </section>
      <section className="py-8 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RelatedBlogCarousel currentSlug="how-our-hosting-service-can-improve-your-websites-speed-and-performance" />
        </div>
      </section>
    </div>
  );
}